import React, { useState } from 'react';
import { Modal, Button, Radio, Space } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload } from '@fortawesome/free-solid-svg-icons';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { sarabunFont } from '../../sarabunFont';
import { useTranslation } from 'react-i18next';

const ScheduleDownloadModal = ({ 
  isVisible, 
  onClose, 
  upcomingSchedule = [], 
  pastSchedule = [],
  student = null
}) => {
  const { t } = useTranslation();
  const [scheduleType, setScheduleType] = useState('upcoming');

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    if (isNaN(date)) return 'Invalid Date';
  
    return new Intl.DateTimeFormat('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    }).format(date);
  };

  const formatTime = (timeString) => {
    if (!timeString) return '';
    const [hours, minutes] = timeString.split(':');
    return `${hours.padStart(2, '0')}:${minutes.padStart(2, '0')}`;
  };

  const calculateEndTime = (startTime, duration) => {
    if (!startTime || !duration) return '';
    const [startHours, startMinutes] = startTime.split(':').map(Number);
    const [durationHours, durationMinutes] = duration.split(':').map(Number);
    let endHours = startHours + durationHours;
    let endMinutes = startMinutes + durationMinutes;
    if (endMinutes >= 60) {
      endHours += Math.floor(endMinutes / 60);
      endMinutes %= 60;
    }
    return `${endHours.toString().padStart(2, '0')}:${endMinutes.toString().padStart(2, '0')}`;
  };

  const loadSarabunFont = (doc) => {
    doc.addFileToVFS('Sarabun.ttf', sarabunFont.src.split('base64,')[1]);
    doc.addFont('Sarabun.ttf', 'Sarabun', 'normal');
    doc.setFont('Sarabun');
  };

  const handleDownloadPDF = () => {
    if (!student) return;

    // Determine which data to use
    let scheduleData = [];
    let filenameSuffix = '';
    
    switch(scheduleType) {
      case 'upcoming':
        scheduleData = upcomingSchedule;
        filenameSuffix = 'upcoming';
        break;
      case 'past':
        scheduleData = pastSchedule;
        filenameSuffix = 'past';
        break;
      case 'all':
        scheduleData = [...upcomingSchedule, ...pastSchedule].sort((a, b) => {
          const dateTimeA = new Date(`${a.date}T${a.time || '00:00'}`);
          const dateTimeB = new Date(`${b.date}T${b.time || '00:00'}`);
          return dateTimeA - dateTimeB;
        });
        filenameSuffix = 'all';
        break;
      default:
        scheduleData = upcomingSchedule;
        filenameSuffix = 'upcoming';
    }

    if (scheduleData.length === 0) {
      Modal.warning({
        title: t('No Data'),
        content: t('No schedule data available for the selected period.')
      });
      return;
    }

    const doc = new jsPDF();
    loadSarabunFont(doc);

    const margin = 20;
    const keyColor = '#005181';
    const pageWidth = doc.internal.pageSize.getWidth();

    const headerImage = new Image();
    headerImage.src = '/images/Theplanner.png';

    headerImage.onload = () => {
      // Header section - logo and contact info side by side
      const logoHeight = 30;
      const logoX = margin;
      const logoY = margin;

      // Calculate logo dimensions
      const imgAspectRatio = headerImage.width / headerImage.height;
      const logoWidth = logoHeight * imgAspectRatio;

      // Add logo
      doc.addImage(headerImage, 'PNG', logoX, logoY, logoWidth, logoHeight);

      // Contact information on the right side
      const contactX = pageWidth - margin;
      const contactStartY = margin + 2;
      
      // Company name
      doc.setFontSize(11);
      doc.setTextColor(0, 0, 0);
      doc.setFont('helvetica', 'bold');
      doc.text('THE PLANNER EDUCATION', contactX, contactStartY, { align: 'right' });
      
      // Address
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.text('The Mercury Ville @Chidlom 3rd floor', contactX, contactStartY + 5, { align: 'right' });
      doc.text('Lumpini Pathumwan Bangkok', contactX, contactStartY + 9, { align: 'right' });
      
      // Website
      doc.text('www.theplannereducation.com', contactX, contactStartY + 13, { align: 'right' });
      
      // Contact numbers
      doc.text('Tel. 02-253-2533 or 095-726-2666 Line: @theplanner', contactX, contactStartY + 17, { align: 'right' });

      // Course title and info - centered
      const centerX = pageWidth / 2;
      const titleY = margin + 45;
      
      // Determine course title & student name
      const studentName = `${student.firstname || ''} ${student.lastname || ''}`.trim();
      const courseTitle = student.class_code || 'Class Schedule';

      // Cursor-based vertical layout
      let cursorY = titleY;

      // 1) Student name (big title)
      doc.setFontSize(14);
      doc.setTextColor(0, 0, 0);
      doc.setFont('helvetica', 'bold');
      doc.text(studentName, centerX, cursorY, { align: 'center' });

      cursorY += 8;

      // 2) Course / class title
      doc.setFontSize(12);
      doc.setFont('helvetica', 'normal');
      doc.text(courseTitle, centerX, cursorY, { align: 'center' });

      cursorY += 7;

      // 3) Total hours
      const totalMinutes = scheduleData.reduce((sum, row) => {
        const [h, m] = (row.hours || '0:00').split(':').map(Number);
        return sum + (h * 60) + m;
      }, 0);

      const totalHoursFormatted = `${Math.floor(totalMinutes / 60)}:${String(totalMinutes % 60).padStart(2, '0')}`;

      doc.setFontSize(12);
      doc.text(`Total Hours: ${totalHoursFormatted} hrs`, centerX, cursorY, { align: 'center' });

      cursorY += 7;

      // 4) Subject(s) with wrapping
      const subjects = [...new Set(scheduleData.map(row => row.subject))].filter(Boolean);
      const subjectText = subjects.length > 0 ? subjects.join(', ') : '';
      const subjectLabel = `Subject(s): ${subjectText}`;

      doc.setFontSize(11);
      doc.setFont('helvetica', 'normal');

      const subjectLines = doc.splitTextToSize(subjectLabel, pageWidth - margin * 2);
      doc.text(subjectLines, centerX, cursorY, { align: 'center' });

      // Move cursor down based on wrapped lines
      cursorY += subjectLines.length * 6;

      // 5) Table starts after header
      const tableStartY = cursorY + 10;


      const columns = [
        { header: t('Day'), dataKey: 'day' },
        { header: t('Date'), dataKey: 'date' },
        { header: t('Subject'), dataKey: 'subject' },
        { header: t('Start Time'), dataKey: 'start_time' },
        { header: t('End Time'), dataKey: 'end_time' },
        { header: t('Hours'), dataKey: 'hours' },
        { header: t('Tutor'), dataKey: 'teacher' },
        { header: t('Room'), dataKey: 'classroom' }
      ];

      const tableData = scheduleData.map(row => ({
        day: new Date(row.date).toLocaleDateString('en-US', { weekday: 'long' }),
        date: formatDate(row.date),
        subject: row.subject || '',
        start_time: formatTime(row.time),
        end_time: calculateEndTime(row.time, row.hours),
        hours: row.hours || '',
        teacher: row.teacher || '',
        classroom: row.classroom || ''
      }));

      doc.autoTable({
        startY: tableStartY,
        head: [columns.map(col => col.header)],
        body: tableData.map(row => columns.map(col => row[col.dataKey])),
        theme: 'grid',
        styles: {
          font: 'Sarabun',
          fontStyle: 'normal',
          fontSize: 8,
          textColor: [0, 0, 0],
          lineColor: [0, 81, 129],
          lineWidth: 0.3,
          cellPadding: 2,
          overflow: 'linebreak',
          halign: 'center',
          valign: 'middle'
        },
        headStyles: {
          fillColor: [0, 81, 129],
          textColor: [255, 255, 255],
          fontStyle: 'bold',
          fontSize: 8,
          halign: 'center'
        },
        alternateRowStyles: {
          fillColor: [245, 245, 245]
        },
        columnStyles: {
          0: { cellWidth: 22, halign: 'center' },  // day
          1: { cellWidth: 24, halign: 'center' },  // date
          2: { cellWidth: 35, halign: 'left' },    // subject (with text wrapping)
          3: { cellWidth: 18, halign: 'center' },  // start time
          4: { cellWidth: 18, halign: 'center' },  // end time
          5: { cellWidth: 15, halign: 'center' },  // hours
          6: { cellWidth: 20, halign: 'center' },  // tutor
          7: { cellWidth: 18, halign: 'center' }   // room
        },
        margin: { top: 20, left: margin, right: margin }
      });

      // Page numbers
      const pageCount = doc.internal.getNumberOfPages();
      for(let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setTextColor(128, 128, 128);
        doc.setFontSize(8);
        doc.text(
          `Page ${i} of ${pageCount}`, 
          doc.internal.pageSize.getWidth() / 2, 
          doc.internal.pageSize.getHeight() - 10, 
          { align: 'center' }
        );
      }

      doc.save(`${student.firstname}_${student.lastname}_schedule_${filenameSuffix}.pdf`);
      onClose();
    };

    headerImage.onerror = () => {
      console.error('Failed to load the header image.');
      Modal.error({
        title: t('Error'),
        content: t('Failed to load header image. Please try again.')
      });
    };
  };

  return (
    <Modal
      open={isVisible}
      onCancel={onClose}
      title={t('Download Class Schedule')}
      footer={[
        <Button key="cancel" onClick={onClose}>
          {t('Cancel')}
        </Button>,
        <Button 
          key="download" 
          type="primary" 
          onClick={handleDownloadPDF}
          icon={<FontAwesomeIcon icon={faDownload} />}
        >
          {t('downloadPDF')}
        </Button>
      ]}
      width={400}
    >
      <Space direction="vertical" style={{ width: '100%' }}>
        <div style={{ marginBottom: '16px' }}>
          <strong>{t('Select schedule type')}:</strong>
        </div>
        <Radio.Group 
          value={scheduleType} 
          onChange={(e) => setScheduleType(e.target.value)}
          style={{ width: '100%' }}
        >
          <Space direction="vertical" style={{ width: '100%' }}>
            <Radio value="upcoming">
              {t('Upcoming Classes')} ({upcomingSchedule.length})
            </Radio>
            <Radio value="past">
              {t('Past Classes')} ({pastSchedule.length})
            </Radio>
            <Radio value="all">
              {t('All Classes')} ({upcomingSchedule.length + pastSchedule.length})
            </Radio>
          </Space>
        </Radio.Group>
      </Space>
    </Modal>
  );
};

export default ScheduleDownloadModal;